/** "Ask a doubt" — the learner types a question in ANY language and gets it
 *  answered in that language (the doubt-clearing half of the two-agent tutor).
 *  Self-contained; drop it anywhere a learner might get stuck. An optional
 *  `context` (the German being practised) grounds the answer.
 *
 *  It is a CONVERSATION, not a single question. The client's report: "it gave
 *  me solution but asking again a follow-up but it did not have an option to
 *  go further." So the exchange stays on screen and the box stays open, each
 *  follow-up going to the same saved thread — which is also what puts it in
 *  the History screen afterwards. */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "../api/http";
import { askDoubt, doubtModels } from "../api/tutor";
import type { DoubtMessage } from "../api/tutor";
import type { AiModel } from "../api/exams";
import { PartnerLine } from "../features/practice/PartnerLine";
import { useT } from "../i18n";
import { Button, Card, Input } from "./ui";

export function AskDoubt({ context }: { context?: string }) {
  const t = useT();
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<DoubtMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<AiModel[]>([]);
  // Which partner will answer. Resolved by the server from the learner's
  // profile preference, not chosen here - see PartnerLine.
  const [activeModel, setActiveModel] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    doubtModels()
      .then((r) => {
        if (cancelled) return;
        setModels(r.models);
        setActiveModel(r.active_model ?? null);
      })
      // No list means no line, which is the same as a deployment with one
      // model. Asking still works.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    const asked = question.trim();
    if (!asked) return;
    setBusy(true);
    setError(null);
    try {
      // No model on the request: the server reads the learner's saved
      // partner. Passing one here would override a preference they set
      // deliberately, from a screen that no longer offers a choice.
      const res = await askDoubt(asked, context, threadId);
      if (!res.configured) {
        setError(t("doubt.off"));
        return;
      }
      // Both turns land together, so a failed call leaves the question in
      // the box to retry rather than stranded above an empty answer.
      setTurns((prev) => [
        ...prev,
        { role: "learner", text: asked },
        { role: "tutor", text: res.answer },
      ]);
      setThreadId(res.thread_id);
      setQuestion("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    // data-tour so the guided tour can point at it: the client's question
    // was how a learner would ever discover this at all.
    <div data-tour="ask.doubt">
      <Card title={t("doubt.title")}>
        <p className="text-sm text-muted">{t("doubt.intro")}</p>

        {turns.length > 0 && (
          <ul className="mt-3 space-y-2" aria-label={t("doubt.conversation")}>
            {turns.map((turn, i) => (
              <li
                key={i}
                role={turn.role === "tutor" ? "note" : undefined}
                className={`max-w-[90%] whitespace-pre-line rounded-control p-3 text-sm ${
                  turn.role === "tutor"
                    ? "bg-canvas text-ink"
                    : "ml-auto bg-brand-soft text-brand-dark"
                }`}
              >
                {turn.text}
              </li>
            ))}
          </ul>
        )}

        {/* Who is answering and what it costs - one line, with the real
            control in the profile. The six-card picker that used to sit
            here took more height than the answer above it, on three
            different screens. */}
        <PartnerLine models={models} activeId={activeModel} />

        <form onSubmit={ask} className="mt-3 space-y-3">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            // The placeholder changes once a conversation exists: the box is
            // no longer asking for a question, it is inviting a follow-up.
            placeholder={
              turns.length ? t("doubt.followUpPlaceholder") : t("doubt.placeholder")
            }
            aria-label={turns.length ? t("doubt.followUp") : t("doubt.title")}
          />
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={busy}>
              {turns.length ? t("doubt.followUp") : t("doubt.ask")}
            </Button>
            {turns.length > 0 && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setTurns([]);
                    setThreadId(null);
                    setError(null);
                  }}
                >
                  {t("doubt.newQuestion")}
                </Button>
                <span className="text-xs text-muted">{t("doubt.saved")}</span>
              </>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}
